import { useEffect, useState } from "react";
import {
  Button,
  Center,
  Code,
  Container,
  CopyButton,
  Group,
  Loader,
  NumberInput,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useLocation } from "wouter";
import {
  createPartnerCode,
  getPartnerCode,
  getPartnerCodeRedemptions,
  updatePartnerCode,
} from "../api";
import Timeago from "../../../Boilerplate/_Shared/Timeago";
import ListPending from "../_Shared/ListPending";

function registerUrl(code) {
  if (!code) return "";
  return `${window.location.origin}/auth/register?code=${encodeURIComponent(code)}`;
}

function RegisterLink({ code }) {
  const url = registerUrl(code);
  if (!url) return null;
  return (
    <Group gap="sm" mt="md" align="center" wrap="wrap">
      <Code style={{ flex: 1, minWidth: 0, wordBreak: "break-all" }}>{url}</Code>
      <CopyButton value={url} timeout={2000}>
        {({ copied, copy }) => (
          <Button
            size="xs"
            variant={copied ? "filled" : "default"}
            color={copied ? "teal" : undefined}
            onClick={copy}
          >
            {copied ? "Copied" : "Copy URL"}
          </Button>
        )}
      </CopyButton>
    </Group>
  );
}

export default function PartnerCodeEdit({ params }) {
  const isEditing = !!params?.id;
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [redemptions, setRedemptions] = useState(null);
  const [, setLocation] = useLocation();

  const form = useForm({
    initialValues: {
      code: "",
      skuCount: 250,
      maxRedemptions: 0,
      expiresAt: "",
      description: "",
    },
  });

  useEffect(() => {
    if (!isEditing) return;
    getPartnerCode(params.id).then((data) => {
      form.setValues({
        code: data.code,
        skuCount: data.creditsToGrant / 100,
        maxRedemptions: data.maxRedemptions,
        expiresAt: data.expiresAt
          ? new Date(data.expiresAt).toISOString().slice(0, 16)
          : "",
        description: data.description || "",
      });
      form.resetDirty();
      setLoading(false);
    });
    getPartnerCodeRedemptions(params.id).then((data) =>
      setRedemptions(Array.isArray(data) ? data : []),
    );
  }, [params?.id]);

  if (loading) {
    return (
      <Center mt="xl">
        <Loader />
      </Center>
    );
  }

  return (
    <Container size="sm">
      <Title order={1} mb="lg">
        {isEditing ? (
          <>
            Code: <Code fz="xl">{form.values.code}</Code>
          </>
        ) : (
          "Create signup code"
        )}
      </Title>

      <form
        onSubmit={form.onSubmit(async (values) => {
          setSaving(true);
          try {
            const payload = {
              code: values.code,
              skuCount: values.skuCount,
              maxRedemptions: values.maxRedemptions,
              expiresAt: values.expiresAt
                ? new Date(values.expiresAt).toISOString()
                : null,
              description: values.description,
            };
            if (isEditing) {
              await updatePartnerCode(params.id, payload);
              notifications.show({ title: "Code updated", color: "green" });
            } else {
              const data = await createPartnerCode(payload);
              setLocation(`/partner/codes/${data.id}`);
              notifications.show({ title: "Code created", color: "green" });
            }
          } finally {
            setSaving(false);
          }
        })}
      >
        <Stack>
          <TextInput
            label="Code"
            description="Merchants register with ?code= this value. Stored uppercase."
            required
            {...form.getInputProps("code")}
          />
          <TextInput
            label="Description"
            description="Shown to the merchant on the register page"
            {...form.getInputProps("description")}
          />
          <NumberInput
            label="SKUs to grant"
            required
            min={1}
            {...form.getInputProps("skuCount")}
          />
          <NumberInput
            label="Max redemptions"
            description="0 = unlimited"
            min={0}
            {...form.getInputProps("maxRedemptions")}
          />
          <TextInput
            label="Expires"
            type="datetime-local"
            {...form.getInputProps("expiresAt")}
          />
          <Group justify="space-between">
            <Button
              variant="subtle"
              onClick={() => setLocation("/partner/codes")}
            >
              Back
            </Button>
            <Button type="submit" loading={saving}>
              {isEditing ? "Save" : "Create"}
            </Button>
          </Group>
        </Stack>
      </form>

      {isEditing && (
        <>
          <Title order={3} mt="xl">
            Redemptions
          </Title>
          <Text size="sm" c="dimmed" mt="md">
            Share this link with merchants:
          </Text>
          <RegisterLink code={form.values.code} />
          <ListPending items={redemptions} emptyText="No redemptions yet.">
            {(rows) => (
            <Table mt="md" striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Email</Table.Th>
                  <Table.Th>Tenant</Table.Th>
                  <Table.Th ta="right">SKUs</Table.Th>
                  <Table.Th>When</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((r) => (
                  <Table.Tr key={r.id}>
                    <Table.Td>{r.redeemedByEmail}</Table.Td>
                    <Table.Td>
                      <Code>{r.redeemedByTenantId}</Code>
                    </Table.Td>
                    <Table.Td ta="right">
                      {(r.creditsGranted || 0) / 100}
                    </Table.Td>
                    <Table.Td>
                      <Timeago date={r.idCreated} />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            )}
          </ListPending>
        </>
      )}
    </Container>
  );
}
